// components/flow/components/config/TagConfig.tsx
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Save, Tag as TagIcon, Plus, X, Search, Edit2, Palette } from 'lucide-react';
import { Button } from '@/components/ui/button.tsx';
import { Input } from '@/components/ui/input.tsx';
import { Label } from '@/components/ui/label.tsx';
import { useToast } from '@/hooks/use-toast.ts';
import {updateNode, getTags, createTag, updateTag, Tag, TagUpdate, TagCreate} from '@/services/api';
import type { FlowNode } from '@/types/flow.ts';
import RandomNextToggle from './shared/RandomNextToggle';

interface TagConfigProps {
    node: FlowNode;
    onClose: () => void;
}

export default function TagConfig({ node, onClose }: TagConfigProps) {
    const { toast } = useToast();
    const queryClient = useQueryClient();

    const [nodeName, setNodeName] = useState(node.data.node_name || '');
    const [selectedTagId, setSelectedTagId] = useState<number | null>(node.data.config.tag_id || null);
    const [searchQuery, setSearchQuery] = useState('');
    const [isRandom, setIsRandom] = useState<boolean>(node.data.is_random ?? false);

    // Estados para criar/editar tag
    const [isCreatingTag, setIsCreatingTag] = useState(false);
    const [editingTagId, setEditingTagId] = useState<number | null>(null);
    const [tagFormData, setTagFormData] = useState({ nome: '', cor: '#FF5733' });

    // Query para buscar tags
    const { data: tags = [], isLoading } = useQuery({
        queryKey: ['tags'],
        queryFn: getTags,
    });

    // Mutation para criar tag
    const createTagMutation = useMutation({
        mutationFn: (data: TagCreate) => createTag(data),
        onSuccess: (newTag) => {
            toast({
                title: 'Tag criada!',
                description: 'A tag foi criada com sucesso.',
            });
            queryClient.invalidateQueries({ queryKey: ['tags'] });
            setSelectedTagId(newTag.id);
            setIsCreatingTag(false);
            setTagFormData({ nome: '', cor: '#FF5733' });
        },
        onError: (error: any) => {
            toast({
                variant: 'destructive',
                title: 'Erro ao criar tag',
                description: error.response?.data?.detail || 'Ocorreu um erro.',
            });
        },
    });

    // Mutation para atualizar tag
    const updateTagMutation = useMutation({
        mutationFn: ({ tagId, data }: { tagId: number; data: TagUpdate }) => updateTag(tagId, data),
        onSuccess: () => {
            toast({
                title: 'Tag atualizada!',
                description: 'A tag foi atualizada com sucesso.',
            });
            queryClient.invalidateQueries({ queryKey: ['tags'] });
            setEditingTagId(null);
            setTagFormData({ nome: '', cor: '#FF5733' });
        },
        onError: (error: any) => {
            toast({
                variant: 'destructive',
                title: 'Erro ao atualizar tag',
                description: error.response?.data?.detail || 'Ocorreu um erro.',
            });
        },
    });

    // Mutation para atualizar node
    const updateMutation = useMutation({
        mutationFn: (data: any) => updateNode(node.data.id, data),
        onSuccess: () => {
            toast({
                title: 'Configurações salvas!',
                description: 'A tag foi configurada com sucesso.',
            });
            queryClient.invalidateQueries({ queryKey: ['funnel-details'] });
            onClose();
        },
        onError: (error: any) => {
            toast({
                variant: 'destructive',
                title: 'Erro ao salvar',
                description: error.response?.data?.detail || 'Ocorreu um erro.',
            });
        },
    });

    // Handler de salvar node
    const handleSave = () => {
        if (!selectedTagId) {
            toast({
                variant: 'destructive',
                title: 'Tag obrigatória',
                description: 'Por favor, selecione uma tag.',
            });
            return;
        }

        updateMutation.mutate({
            nome: nodeName,
            tag_id: selectedTagId,
            is_random: isRandom,
        });
    };

    // Handler de criar tag
    const handleCreateTag = () => {
        if (!tagFormData.nome.trim()) {
            toast({
                variant: 'destructive',
                title: 'Nome obrigatório',
                description: 'Por favor, insira um nome para a tag.',
            });
            return;
        }

        createTagMutation.mutate(tagFormData);
    };

    // Handler de editar tag
    const handleEditTag = (tag: Tag) => {
        setEditingTagId(tag.id);
        setTagFormData({ nome: tag.nome, cor: tag.cor });
        setIsCreatingTag(false);
    };

    // Handler de atualizar tag
    const handleUpdateTag = () => {
        if (!editingTagId) return;

        if (!tagFormData.nome.trim()) {
            toast({
                variant: 'destructive',
                title: 'Nome obrigatório',
                description: 'Por favor, insira um nome para a tag.',
            });
            return;
        }

        updateTagMutation.mutate({
            tagId: editingTagId,
            data: tagFormData
        });
    };

    // Cancelar formulário
    const handleCancelForm = () => {
        setIsCreatingTag(false);
        setEditingTagId(null);
        setTagFormData({ nome: '', cor: '#FF5733' });
    };

    // Filtrar tags por busca
    const filteredTags = tags.filter(tag =>
        tag.nome.toLowerCase().includes(searchQuery.toLowerCase())
    );

    // Tag selecionada
    const selectedTag = tags.find(tag => tag.id === selectedTagId);

    const isSaving = updateMutation.isPending || createTagMutation.isPending || updateTagMutation.isPending;

    return (
        <>
            <div className="flex-1 overflow-auto">
                <div className="p-6 space-y-6">
                    {/* Nome do Node */}
                    <div>
                        <Label htmlFor="node-name">Nome do Node (opcional)</Label>
                        <Input
                            id="node-name"
                            value={nodeName}
                            onChange={(e) => setNodeName(e.target.value)}
                            placeholder="Adicionar Tag"
                            className="mt-1.5"
                            disabled={isSaving}
                        />
                    </div>

                    {/* Tag Selecionada */}
                    {selectedTag && !isCreatingTag && !editingTagId && (
                        <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <div
                                        className="w-4 h-4 rounded-full border-2 border-white shadow-sm"
                                        style={{ backgroundColor: selectedTag.cor }}
                                    />
                                    <div>
                                        <p className="text-sm font-medium text-gray-900">Tag Selecionada</p>
                                        <p className="text-xs text-gray-500">{selectedTag.nome}</p>
                                    </div>
                                </div>
                                <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => setSelectedTagId(null)}
                                    disabled={isSaving}
                                >
                                    <X className="w-4 h-4" />
                                </Button>
                            </div>
                        </div>
                    )}

                    {/* Formulário de Criar/Editar Tag */}
                    {(isCreatingTag || editingTagId) && (
                        <div className="p-4 bg-orange-50 rounded-lg border border-orange-200 space-y-4">
                            <div className="flex items-center justify-between">
                                <h3 className="text-sm font-semibold text-orange-900">
                                    {editingTagId ? 'Editar Tag' : 'Criar Nova Tag'}
                                </h3>
                                <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={handleCancelForm}
                                    disabled={isSaving}
                                >
                                    <X className="w-4 h-4" />
                                </Button>
                            </div>

                            <div className="space-y-3">
                                {/* Nome da Tag */}
                                <div>
                                    <Label className="text-xs">Nome da Tag</Label>
                                    <Input
                                        value={tagFormData.nome}
                                        onChange={(e) => setTagFormData({ ...tagFormData, nome: e.target.value })}
                                        placeholder="Ex: VIP, Interessado"
                                        className="mt-1"
                                        disabled={isSaving}
                                    />
                                </div>

                                {/* Cor da Tag */}
                                <div>
                                    <Label className="text-xs">Cor da Tag</Label>
                                    <div className="flex gap-2 mt-1">
                                        <input
                                            type="color"
                                            value={tagFormData.cor}
                                            onChange={(e) => setTagFormData({ ...tagFormData, cor: e.target.value })}
                                            className="w-12 h-10 rounded border border-gray-300 cursor-pointer"
                                            disabled={isSaving}
                                        />
                                        <Input
                                            value={tagFormData.cor}
                                            onChange={(e) => setTagFormData({ ...tagFormData, cor: e.target.value })}
                                            placeholder="#FF5733"
                                            className="flex-1"
                                            disabled={isSaving}
                                        />
                                    </div>
                                </div>

                                {/* Preview */}
                                <div className="flex items-center gap-2 p-2 bg-white rounded border border-gray-200">
                                    <Palette className="w-4 h-4 text-gray-400" />
                                    <span className="text-xs text-gray-500">Preview:</span>
                                    <div className="flex items-center gap-2 px-3 py-1 rounded-full" style={{ backgroundColor: tagFormData.cor + '20' }}>
                                        <div
                                            className="w-3 h-3 rounded-full border-2 border-white shadow-sm"
                                            style={{ backgroundColor: tagFormData.cor }}
                                        />
                                        <span className="text-xs font-medium" style={{ color: tagFormData.cor }}>
                                            {tagFormData.nome || 'Nome da Tag'}
                                        </span>
                                    </div>
                                </div>

                                {/* Botão de Ação */}
                                <Button
                                    onClick={editingTagId ? handleUpdateTag : handleCreateTag}
                                    disabled={isSaving || !tagFormData.nome.trim()}
                                    className="w-full bg-orange-600 hover:bg-orange-700"
                                >
                                    {isSaving ? 'Salvando...' : (editingTagId ? 'Atualizar Tag' : 'Criar Tag')}
                                </Button>
                            </div>
                        </div>
                    )}

                    {/* Seletor de Tag */}
                    {!isCreatingTag && !editingTagId && (
                        <div>
                            <div className="flex items-center justify-between mb-2">
                                <Label>Selecionar Tag *</Label>
                                <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => setIsCreatingTag(true)}
                                    disabled={isSaving}
                                >
                                    <Plus className="w-3.5 h-3.5 mr-1.5" />
                                    Nova Tag
                                </Button>
                            </div>

                            {/* Busca */}
                            <div className="relative mt-1.5 mb-3">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                                <Input
                                    placeholder="Buscar tags..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    className="pl-10"
                                    disabled={isSaving}
                                />
                            </div>

                            {/* Lista de Tags */}
                            {isLoading ? (
                                <div className="flex items-center justify-center py-8">
                                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
                                </div>
                            ) : filteredTags.length === 0 ? (
                                <div className="text-center py-8 border-2 border-dashed border-gray-200 rounded-lg">
                                    <TagIcon className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                                    <p className="text-sm text-gray-600 font-medium">
                                        {searchQuery ? 'Nenhuma tag encontrada' : 'Nenhuma tag disponível'}
                                    </p>
                                    <p className="text-xs text-gray-500 mt-1 mb-3">
                                        {searchQuery ? 'Tente outra busca' : 'Crie sua primeira tag'}
                                    </p>
                                    {!searchQuery && (
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={() => setIsCreatingTag(true)}
                                        >
                                            <Plus className="w-3.5 h-3.5 mr-1.5" />
                                            Criar Primeira Tag
                                        </Button>
                                    )}
                                </div>
                            ) : (
                                <div className="space-y-2 max-h-[400px] overflow-y-auto border border-gray-200 rounded-lg p-2">
                                    {filteredTags.map((tag) => (
                                        <div
                                            key={tag.id}
                                            className={`
                                                w-full flex items-center gap-3 p-3 rounded-lg transition-all
                                                ${selectedTagId === tag.id
                                                ? 'bg-orange-50 border-2 border-orange-500 shadow-sm'
                                                : 'bg-white border border-gray-200'
                                            }
                                            `}
                                        >
                                            <button
                                                onClick={() => setSelectedTagId(tag.id)}
                                                disabled={isSaving}
                                                className="flex-1 flex items-center gap-3 text-left"
                                            >
                                                <div
                                                    className="w-4 h-4 rounded-full border-2 border-white shadow-sm flex-shrink-0"
                                                    style={{ backgroundColor: tag.cor }}
                                                />
                                                <span className={`text-sm font-medium ${
                                                    selectedTagId === tag.id ? 'text-orange-900' : 'text-gray-700'
                                                }`}>
                                                    {tag.nome}
                                                </span>
                                            </button>

                                            <div className="flex items-center gap-1">
                                                {selectedTagId === tag.id && (
                                                    <div className="w-5 h-5 bg-orange-500 rounded-full flex items-center justify-center">
                                                        <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                                        </svg>
                                                    </div>
                                                )}
                                                <Button
                                                    size="sm"
                                                    variant="ghost"
                                                    onClick={() => handleEditTag(tag)}
                                                    disabled={isSaving}
                                                    className="h-7 w-7 p-0"
                                                >
                                                    <Edit2 className="w-3.5 h-3.5" />
                                                </Button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {!isCreatingTag && !editingTagId && (
                        <RandomNextToggle
                            isRandom={isRandom}
                            onChange={setIsRandom}
                            disabled={isSaving}
                        />
                    )}

                    {/* Info Helper */}
                    {!isCreatingTag && !editingTagId && (
                        <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
                            <p className="text-xs text-orange-900">
                                💡 <strong>Dica:</strong> Esta tag será adicionada ao lead quando ele passar por este node no fluxo.
                            </p>
                        </div>
                    )}
                </div>
            </div>

            {/* Footer */}
            <div className="border-t border-gray-200 px-6 py-4 flex justify-end gap-3 flex-shrink-0">
                <Button variant="outline" onClick={onClose} disabled={isSaving}>
                    Cancelar
                </Button>
                <Button
                    onClick={handleSave}
                    disabled={isSaving || !selectedTagId || isCreatingTag || !!editingTagId}
                >
                    {isSaving ? (
                        'Salvando...'
                    ) : (
                        <>
                            <Save className="w-4 h-4 mr-2" />
                            Salvar Configurações
                        </>
                    )}
                </Button>
            </div>
        </>
    );
}